/* global runtime */
// AI advisor report generation: model assembly, saved reports, PDF export.
// Loaded as a classic script before backend.jsx and shares its binding scope.
//
// This file owns no accounting math. It gathers the models the app has already
// derived for a year — report result, trial balance, monthly chart, budget vs
// actual, the advisor model, the sales model, the home dashboard — and hands
// them to the pure builder in aiAdvisorReports.jsx. Anything a report shows can
// therefore be traced back to the screen that already shows it.

// How much of the year is booked. A past year is complete, a future year has
// nothing, and the running year counts through the current month. The budget
// model owns the same question, so we reuse its helper when it is loaded.
const advisorReportMonthsElapsed = (year, now = new Date()) => {
  const plan = globalThis.PlummeAiAdvisorPlan;
  if (plan && typeof plan.monthsElapsedIn === 'function') return plan.monthsElapsedIn(year, now);
  if (year < now.getFullYear()) return 12;
  if (year > now.getFullYear()) return 0;
  return now.getMonth() + 1;
};

// The advisor forecast is a running balance of BOOKED figures, which leaves
// every month ahead of today standing flat. The user's own planned payments and
// receipts are the one thing that legitimately fills those months, and the plan
// model already knows how to fold them in -- the report just has to ask.
// Months changed this way come back marked `planned`, never `confirmed`.
const advisorReportForecast = (advisor, year) => {
  const plan = globalThis.PlummeAiAdvisorPlan;
  const forecast = safeArray(advisor && advisor.forecast);
  if (!plan || typeof plan.applyEventsToForecast !== 'function' || !forecast.length) return forecast;
  return plan.applyEventsToForecast({ forecast, events: getPlannedCashEvents(), year }).forecast;
};

// The advisor model derives its own report result with imported sales history
// folded in. The report document must use the very same expression, or a board
// report and the advisor tab would print different revenue for one year.
const advisorReportResultFor = (state, year) =>
  addImportedSalesHistoryToAdvisorReport(deriveReportResult(state, year), state, year);

// Equity ratio is a balance-sheet measure, so the dashboard's operational
// proxy is not suitable for a board report. Reuse the reconciled equity read
// model for Norwegian books and leave the value unavailable for countries
// whose account ranges have not been implemented there yet.
const advisorReportEquityReconciliation = (state, year, monthsElapsed) => {
  const country = typeof normalizeChartCountry === 'function'
    ? normalizeChartCountry(state.settings)
    : String(state.settings?.country || 'NO').toUpperCase();
  const builder = globalThis.buildEquityReconciliationView;
  const month = Math.min(12, Math.max(0, Number(monthsElapsed) || 0));
  if (country !== 'NO' || typeof builder !== 'function' || month < 1) return null;
  return builder({
    periodId: `${year}-${String(month).padStart(2, '0')}`,
    state,
  });
};

const buildAiAdvisorReportContext = (year) => {
  const state = mergedRuntimeState();
  if (window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state, { allowImportedStatistics: true })) {
    return { ...window.PlummeAccountingBasisCoverage.blockedReadModel('advisor_report', state.accountingBasisCoverage), year };
  }
  const report = advisorReportResultFor(state, year);
  const previousReport = advisorReportResultFor(state, year - 1);
  // Only the trial balance and the chart are taken from the cached reports
  // model. Its `result` is the plain derivation without imported sales history,
  // and mixing the two would be the disagreement this file exists to avoid.
  const reports = getReportsModel({ year, includeResult: false }) || {};
  // The two earlier years are pulled for the rolling window only, so the trial
  // balance -- by far the most expensive part -- is switched off for them.
  const chartOnly = (targetYear) =>
    (getReportsModel({ year: targetYear, includeResult: false, includeTrial: false }) || {}).chart || null;
  const monthsElapsed = advisorReportMonthsElapsed(year);
  const advisor = getAiAdvisorModel({ year });
  return {
    year,
    reportingBasis: reports.reportingBasis || null,
    monthsElapsed,
    settings: state.settings || {},
    report,
    previousReport,
    previousYearAvailable: aiAdvisorPreviousYearAvailable(state, year, previousReport),
    trial: reports.trial || [],
    chart: reports.chart || [],
    previousChart: chartOnly(year - 1),
    previous2Chart: chartOnly(year - 2),
    advisor: advisor ? { ...advisor, forecast: advisorReportForecast(advisor, year) } : advisor,
    plan: getAiAdvisorPlanModel({ year }),
    sales: getAiAdvisorSalesModel({ year }),
    home: getHomeDashboardModel(),
    equityReconciliation: advisorReportEquityReconciliation(state, year, monthsElapsed),
    partyInsights: aiAdvisorImportedPartyInsights(state, year, report),
  };
};

const getAiAdvisorReportTemplates = () => safeArray(globalThis.PlummeAiAdvisorReports?.REPORT_TEMPLATES)
  .map((template) => ({
    id: template.id,
    icon: template.icon,
    lengthKey: template.lengthKey,
    sourceKeys: template.sourceKeys,
  }));

// Only dated native purchases can support a quarterly supplier ranking.
const advisorQuarterSuppliers = (state, year, months) => {
  const totals = new Map();
  safeArray(state.expenses).filter((expense) => isActiveExpense(expense) && !isFixedAssetExpense(expense)
    && reportDateInRange(expense.date, year, { months }) && expense.supplierName).forEach((expense) => {
    const name = String(expense.supplierName);
    totals.set(name, (totals.get(name) || 0) + dashboardCostExpense(expense));
  });
  const suppliers = [...totals].map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount).slice(0, 5);
  return { suppliers, customers: [], scope: suppliers.length ? 'selected-quarter' : 'none', limitedToTop: 5, nativeOnly: true };
};

const getAiAdvisorReportDoc = ({ templateId, year, quarter, month } = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  if (!globalThis.PlummeAiAdvisorReports) return null;
  const state = mergedRuntimeState();
  const monthlyPeriod = templateId === 'manedsrapport'
    ? window.PlummeAdvisorReportPeriod.resolveMonth(month)
    : null;
  const activeYear = monthlyPeriod?.year || Number(year || state.activeYear) || new Date().getFullYear();
  // The context is the expensive half — it pulls the trial balance and both
  // years of the report result. Every template for one year reuses it.
  const period = monthlyPeriod || window.PlummeAdvisorReportPeriod.resolve(activeYear, quarter);
  const periodCacheKey = period.id || period.quarter || 'year';
  const context = cachedDerivedModel(`advisorReportCtx:${activeYear}:${periodCacheKey}`, () => {
    const annual = buildAiAdvisorReportContext(activeYear);
    if (annual?.blocked) return annual;
    const readers = {
      result: (y, months) => addImportedSalesHistoryToAdvisorReport(deriveReportResult(state, y, { months }), state, y, { months }),
      trial: (y, months) => getReportsModel({ year: y, months, includeResult: false, includeChart: false }).trial,
      budget: (y) => getBudgetModel({ year: y }), plan: window.PlummeAiAdvisorPlan.buildBudgetVsActual,
      sales: (y, months) => buildAiAdvisorSalesModelForYear(state, y, { months }),
      parties: (y, months) => advisorQuarterSuppliers(state, y, months),
    };
    return monthlyPeriod
      ? window.PlummeAdvisorReportPeriod.scopeMonth(annual, period, readers)
      : window.PlummeAdvisorReportPeriod.scope(annual, period, readers);
  });
  if (context?.blocked) return context;
  // The timestamp belongs to this document, not to the cached context: two
  // reports generated hours apart in one session must not claim the same
  // generation time, since that is what the saved list sorts by.
  // The most recent saved snapshot of the same report and year is what "since
  // last time" is measured against. It lives outside the cached context because
  // it changes every time the user saves.
  const previousDoc = latestSavedReportDoc(templateId, activeYear, period);
  return globalThis.PlummeAiAdvisorReports.buildReportDoc(templateId, { ...context, previousDoc, generatedAt: nowIso() });
};

const advisorReportPeriodIdentity = (period) => period?.id
  ? `month:${period.id}`
  : period?.quarter ? `quarter:${period.quarter}` : 'year';

const latestSavedReportDoc = (templateId, year, period = null) => {
  const identity = advisorReportPeriodIdentity(period);
  const previous = getSavedAiAdvisorReports()
    .find((entry) => entry.templateId === templateId && entry.year === year && entry.doc
      && advisorReportPeriodIdentity(entry.doc.period) === identity);
  return previous ? previous.doc : null;
};

// ---------------- Saved reports ----------------
// A saved report is a snapshot, not a live view: it keeps the blocks it was
// generated from, so reopening it in March shows what the board actually read
// in February. Regenerating is an explicit action, never a side effect.
const ADVISOR_REPORT_LIMIT = 50;
const advisorReportLimitError = () => new Error(t('adv.rpt.saved.limitError', { limit: ADVISOR_REPORT_LIMIT }));
const normalizeSavedAdvisorReport = (entry = {}) => stripUndefinedDeep({
  id: String(entry.id || createId('report')),
  templateId: String(entry.templateId || '').slice(0, 40),
  year: Number(entry.year) || new Date().getFullYear(),
  title: String(entry.title || '').trim().slice(0, 160),
  doc: entry.doc && typeof entry.doc === 'object' ? window.PlummeAdvisorReportStorage.decodeValue(entry.doc) : null,
  decisions: safeArray(entry.decisions).map((id) => String(id).slice(0, 80)).slice(0, 40),
  // The narrative is stored with the snapshot it was written about. Re-reading
  // a saved report must show the wording the reader actually got, not a fresh
  // one written against the same blocks months later.
  narrative: entry.narrative && typeof entry.narrative === 'object'
    ? window.PlummeAdvisorReportStorage.decodeValue(entry.narrative)
    : null,
  generatedAt: entry.generatedAt || nowIso(),
  updatedAt: nowIso(),
});

let advisorReportCacheBusinessId = '';
let advisorReportCache = [];
const sortSavedAdvisorReports = (reports) => safeArray(reports).slice()
  .sort((a, b) => String(b.generatedAt).localeCompare(String(a.generatedAt)));
const legacySavedAdvisorReports = () => safeArray(mergedRuntimeState().advisorReports).map(normalizeSavedAdvisorReport);
const advisorReportsCloudReady = () => Boolean(runtime.db && runtime.activeBusinessId && runtime.auth?.currentUser);
const advisorReportsCollection = () => runtime.db.collection('businesses').doc(runtime.activeBusinessId).collection('advisorReports');
const cachedAdvisorReports = () => advisorReportCacheBusinessId === runtime.activeBusinessId ? advisorReportCache : [];
const setAdvisorReportCache = (reports) => {
  advisorReportCacheBusinessId = runtime.activeBusinessId || '';
  advisorReportCache = sortSavedAdvisorReports(reports).slice(0, ADVISOR_REPORT_LIMIT);
  return advisorReportCache;
};
const advisorReportForStorage = (entry) => window.PlummeAdvisorReportStorage.encodeValue(stripUndefinedDeep({
  ...entry,
  storageVersion: 1,
  updatedByUserId: runtime.auth?.currentUser?.uid || undefined,
  updatedBySource: 'client',
}));

const getSavedAiAdvisorReports = () => {
  const byId = new Map([...cachedAdvisorReports(), ...legacySavedAdvisorReports()].map((entry) => [entry.id, entry]));
  return sortSavedAdvisorReports([...byId.values()]);
};
const getAiAdvisorReportLimit = () => ADVISOR_REPORT_LIMIT;

const loadSavedAiAdvisorReports = async () => {
  const legacy = legacySavedAdvisorReports();
  if (!advisorReportsCloudReady()) return setAdvisorReportCache(legacy);
  const businessId = runtime.activeBusinessId;
  const snapshot = await advisorReportsCollection().get();
  if (runtime.activeBusinessId !== businessId) return [];
  const cloud = snapshot.docs.map((document) => normalizeSavedAdvisorReport({ id: document.id, ...document.data() }));
  const byId = new Map(cloud.map((entry) => [entry.id, entry]));
  const missingLegacy = legacy.filter((entry) => !byId.has(entry.id));
  const legacyToMigrate = missingLegacy.slice(0, Math.max(0, ADVISOR_REPORT_LIMIT - cloud.length));
  if (legacyToMigrate.length) {
    const batch = runtime.db.batch();
    legacyToMigrate.forEach((entry) => batch.set(advisorReportsCollection().doc(entry.id), advisorReportForStorage(entry)));
    await batch.commit();
    legacyToMigrate.forEach((entry) => byId.set(entry.id, entry));
  }
  if (legacy.length) {
    const migratedIds = new Set([...cloud, ...legacyToMigrate].map((entry) => entry.id));
    await updateStateAndPersist((state) => ({
      ...state,
      advisorReports: safeArray(state.advisorReports).filter((entry) => !migratedIds.has(entry?.id)),
    }));
  }
  return setAdvisorReportCache([...byId.values()]);
};

const saveAiAdvisorReport = async (entry = {}) => {
  const next = normalizeSavedAdvisorReport(entry);
  if (!next.templateId || !next.doc) throw new Error(t('svc.advisorReportIncomplete'));
  if (advisorReportsCloudReady()) {
    const snapshot = await advisorReportsCollection().get();
    const alreadySaved = snapshot.docs.some((document) => document.id === next.id);
    if (!alreadySaved && snapshot.size >= ADVISOR_REPORT_LIMIT) throw advisorReportLimitError();
    await advisorReportsCollection().doc(next.id).set(advisorReportForStorage(next));
    setAdvisorReportCache([next, ...cachedAdvisorReports().filter((item) => item.id !== next.id)]);
  } else {
    await updateStateAndPersist((state) => {
      const list = safeArray(state.advisorReports).map(normalizeSavedAdvisorReport);
      if (!list.some((item) => item.id === next.id) && list.length >= ADVISOR_REPORT_LIMIT) throw advisorReportLimitError();
      const merged = [next, ...list.filter((item) => item.id !== next.id)];
      return { ...state, advisorReports: merged.map(advisorReportForStorage) };
    });
  }
  return next;
};

const deleteAiAdvisorReport = async (id) => {
  const key = String(id || '').trim();
  if (!key) return;
  if (advisorReportsCloudReady()) await advisorReportsCollection().doc(key).delete();
  setAdvisorReportCache(cachedAdvisorReports().filter((item) => item.id !== key));
  if (safeArray(mergedRuntimeState().advisorReports).some((item) => item?.id === key)) {
    await updateStateAndPersist((state) => ({
      ...state,
      advisorReports: safeArray(state.advisorReports).filter((item) => item.id !== key),
    }));
  }
};

// ---------------- PDF export ----------------
// The page resolves every catalogue key before calling this, because the
// catalogue lives in the browser and the renderer does not. What crosses the
// wire is therefore finished text and plain numbers — the same trust model the
// existing income-statement export already uses.
const downloadAiAdvisorReportPdf = async (payload = {}) => {
  const report = {
    reportType: 'advisor-report',
    locale: PlummeI18n.getLocale(),
    year: Number(payload.year) || new Date().getFullYear(),
    title: String(payload.title || '').slice(0, 120),
    periodLabel: String(payload.periodLabel || '').slice(0, 60),
    blockSections: safeArray(payload.sections),
    presentation: payload.presentation,
  };
  const result = await authorizedPost('sendReportPdfEmailHttp', { action: 'download', report });
  if (payload.presentation && result?.presentationVersion !== payload.presentation.version) throw new Error(t('adv.rpt.period.pdfUnavailable'));
  if (!result?.pdfBase64 || !result?.fileName) throw new Error(t('svc.pdfFailed'));
  // Handing the blob to the browser here rather than in the page follows
  // backend-annual-documents.jsx, and keeps the one base64-to-blob helper in
  // the scope that already owns it.
  const url = URL.createObjectURL(pdfBase64ToBlob(result.pdfBase64));
  const link = document.createElement('a');
  link.href = url;
  const periodSuffix = /^\d{4}-\d{2}$/.test(String(payload.periodId || ''))
    ? '-' + payload.periodId
    : payload.quarter ? '-Q' + payload.quarter : '';
  link.download = periodSuffix ? result.fileName.replace(/\.pdf$/i, periodSuffix + '.pdf') : result.fileName;
  link.click();
  URL.revokeObjectURL(url);
  return { fileName: result.fileName };
};

const downloadAiAdvisorReportPptx = async (payload = {}) => {
  return window.buildAdvisorPptx(payload);
};

// ---------------- Executive narrative ----------------
// The model writes the reading of the report; the report keeps owning every
// figure. We post the same resolved sections the PDF export uses, so the
// narrative can only ever discuss numbers the document already shows — and the
// backend rejects the answer outright if it names one that is not in there.
//
// This is deliberately not part of getAiAdvisorReportDoc: the report must open
// instantly and stay complete when the model is slow, refuses, or is off.
const fetchAiAdvisorReportNarrative = async ({ sections, templateName, periodLabel } = {}) => {
  const ok = await initFirebase();
  if (!ok) throw new Error(t('svc.noFirebase'));
  if (!runtime.auth?.currentUser) throw new Error(t('svc.needLoginAdvisor'));
  const payload = await authorizedPost('getAiAdvisorReportNarrativeHttp', {
    sections: safeArray(sections),
    templateName: String(templateName || '').slice(0, 120),
    periodLabel: String(periodLabel || '').slice(0, 80),
    // Main goal, risk level and accountant involvement steer the summary's
    // emphasis; the backend re-validates every value against its option lists.
    preferences: getAiAdvisorProfile(),
    locale: PlummeI18n.getLocale(),
    businessId: runtime.activeBusinessId || undefined,
  });
  return payload.narrative || null;
};

// ---------------- Task from a recommended action ----------------
// A report action is one of the advisor's own recommendations. Creating a task
// from it therefore writes to the recommendation-status store the advisor page
// already reads, rather than starting a second, parallel to-do list that could
// disagree with it. The status lands in the `planned` bucket of the task centre.
//
// Two details are load-bearing. A snoozed status without a date is treated as
// expired on the next read, so the follow-up date is required, not optional --
// the caller shows it to the user rather than letting it be a hidden default.
// And the fingerprint pins the figure the advice was about: when the overdue
// amount changes, this stops being the same task and reopens by itself.
const ADVISOR_REPORT_TASK_DAYS = 14;
const advisorReportFollowUpDate = (days = ADVISOR_REPORT_TASK_DAYS, now = new Date()) => {
  const date = new Date(now);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const createAiAdvisorReportTask = async ({ recId, impact, note, followUpDate } = {}) => {
  const key = String(recId || '').trim();
  if (!key) throw new Error(t('svc.advisorStatusMissingId'));
  const snoozeUntil = followUpDate || advisorReportFollowUpDate();
  await setAdvisorRecommendationStatus(key, {
    status: 'snoozed',
    snoozeUntil,
    note: note || undefined,
    fingerprint: globalThis.PlummeAiAdvisorStatus?.fingerprintOf({ id: key, impact }),
  });
  return { recId: key, snoozeUntil };
};
